import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faSearch, faHistory, faFilter, faTimes } from '@fortawesome/free-solid-svg-icons';
import ProductCardList from '../../components/common/ProductCardList';
import { useQueryParamsHandler } from 'odfjell-query-manager';
import { PRODUCT_PARAMS } from '../../utils/queryParamsUtils';
import useGetProduct from '../../api/useGetProduct';
import useGetProductsByCategory from '../../api/useGetProductsByCategory';
import { useAppSettings } from '../../hooks/useAppSettings';
import { buildApiUrl } from '../../utils/apiUtils';
import axios from 'axios';
import useUpdateProduct from '../../api/useUpdateProduct';
import { buildMinimalUpdatePayload, validateProductForUpsert } from '../../utils/productUpdateConverter';
import { formatCategoryName } from '../../utils/categoryNameFormatter';
import { ProductDetail, ProductListItem } from '../../types/ProductTypes';
import TabMenu, { TabType } from '../../components/product/TabMenu';
import { addToProductHistory, getProductHistory } from '../../utils/productHistory';
import useProductQuery from '../../api/useProductQuery';
import GeneralInformationSection from '../../components/product/sections/GeneralInformationSection';
import OperationalHeatingSection from '../../components/product/sections/OperationalHeatingSection';
import RegulationsSection from '../../components/product/sections/RegulationsSection';
import USCGCompatibilitySection from '../../components/product/sections/USCGCompatibilitySection';
import PhysicalPropertiesSection from '../../components/product/sections/PhysicalPropertiesSection';
import RulesRegulationsSection from '../../components/product/sections/RulesRegulationsSection';
import ProceduresSection from '../../components/product/sections/ProceduresSection';
import OtherProceduresSection from '../../components/product/sections/OtherProceduresSection';
import ORCASection from '../../components/product/sections/ORCASection';
import CommentsSection from '../../components/product/sections/CommentsSection';
import ProductHeader from '../../components/product/ProductHeader';
import { ProductEditContextProvider, useProductEdit } from '../../context/ProductEditContext';
import { useEditMode } from '../../context/EditModeContext';
import { useAuthStore } from '../../context/AuthContext';
import { PageWrapper, PageContainer } from '../../components/common/PageWrapper.styles';
import {
    ProductPageContainer,
    LoadingContainer,
    LoadingSpinner,
    ErrorContainer,
    ErrorActions,
    RetryButton,
    BackButton,
    NotFoundContainer,
    DefaultProductLabel,
    ProductContent,
    ProductLayout,
    Sidebar,
    MainContent,
    ProductSections,
    SplitViewContainer,
    SplitViewProductListPanel,
    SplitViewCloseButton,
    SplitViewTabBar,
    SplitViewTab,
    SplitViewTabBarRight,
    SplitViewFilterButton,
    SplitViewSearchContainer,
    SplitViewSearchInput,
    SplitViewMainContent,
    SplitViewContentLoading,
    SplitViewFilterPanel,
    SplitViewFilterSection,
    SplitViewFilterSectionTitle,
    SplitViewFilterCheckboxGroup,
} from './ProductPage.components';
import { sortByRelevance } from '../../utils/searchUtils';
import { useChartererStore, enrichWithChartererNames } from '../../context/ChartererContext';
import CoatingCompatibilitiesSection from 'src/components/product/sections/CoatingCompatibilitiesSection';
import MaterialSafetyDataSheetsSection from 'src/components/product/sections/MaterialSafetyDataSheetsSection';
import SafetyInformationSection from 'src/components/product/sections/SafetyInformationSection';
import ExternalIdsSection from 'src/components/product/sections/ExternalIdsSection';
import ProductChangeLogSection from 'src/components/product/sections/ProductChangeLogSection';

const ProductPageShell: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <PageWrapper>
        <PageContainer>
            <ProductPageContainer>{children}</ProductPageContainer>
        </PageContainer>
    </PageWrapper>
);

const ProductPage: React.FC = () => {
    const navigate = useNavigate();
    const { queryParamUpdate, getQueryParamValueFromUrl, triggerQueryParamsUpdate } = useQueryParamsHandler();
    const [productId, setProductId] = useState<string | null>(null);
    const [activeTab, setActiveTab] = useState<TabType>(() => {
        // Initialize from URL, default to 'all' if not present or invalid
        const urlTab = new URLSearchParams(window.location.search).get(PRODUCT_PARAMS.TAB);
        const validTabs: TabType[] = [
            'all',
            'general',
            'physical-properties',
            'rules-regulations',
            'operational',
            'regulations',
            'uscg',
            'coating',
            'safety',
            'cleaning',
            'other-procedures',
            'external',
            // 'orca', // Hidden - not in use
            'changelog',
        ];
        return validTabs.includes(urlTab as TabType) ? (urlTab as TabType) : 'all';
    });
    const { getProduct, isLoading, error, product } = useGetProduct(productId || '');
    const { products: rawRelatedProducts } = useGetProductsByCategory(product?.categoryId);
    const { updateProduct } = useUpdateProduct();
    const { getPendingChanges, clearPendingChanges, mergePendingChanges, setHasUnsavedChanges, hasUnsavedChanges } = useProductEdit();
    const { isInEditMode, setEditMode } = useEditMode();
    const { canEdit, canEditChemGuard } = useAuthStore();
    const { charterers } = useChartererStore();
    const hasWriterRole = canEdit || canEditChemGuard;
    const { settings: appSettings } = useAppSettings();
    const relatedProducts = useMemo(
        () => enrichWithChartererNames(rawRelatedProducts as any[], charterers),
        [rawRelatedProducts, charterers]
    );
    const [isSaving, setIsSaving] = useState(false);
    const [isCreatingNewProduct, setIsCreatingNewProduct] = useState(false);
    const [isCreatingNewCategory, setIsCreatingNewCategory] = useState(false);

    // State for category product's Contains — populated after categoryProduct is resolved
    const [categoryProductContains, setCategoryProductContains] = useState<string | null>(null);

    // Blank product used when creating a new category from the Products list page
    const blankProduct: ProductDetail = useMemo(() => ({
        id: 0,
        categoryId: 0,
        lastUpdatedUtc: new Date().toISOString(),
        synonyms: [],
        procedures: [],
        coatingCompatibilities: [],
        externalIds: [],
        comments: [],
        stowageRules: [],
        temperatureRequirements: null,
        physicalData: null,
        gasPhysicalData: null,
        category: { id: 0, name: '', externalIds: [], regulations: null } as any,
        tradeName: '',
        mainHazards: '',
        charterer: { id: 0, name: '', code: '', externalId: 0, lastUpdatedUtc: new Date().toISOString() },
        regulations: {} as any,
        materialSafetyDataSheet: null,
        materialSafetyDataSheets: [],
        productDocuments: [],
        draegerTubes: [],
        isNonCargo: false,
        isActive: false,
        isApproved: false,
    }), []);
    const [isSplitView, setIsSplitView] = useState(true);

    // Split view panel state
    type SplitListMode = 'search' | 'history';
    const [splitListMode, setSplitListMode] = useState<SplitListMode>(() => {
        const savedSearch = sessionStorage.getItem('cbp.lastSearch');
        return savedSearch?.trim() ? 'search' : 'history';
    });
    const [splitSearchTerm, setSplitSearchTerm] = useState<string>(() => {
        return sessionStorage.getItem('cbp.lastSearch') || '';
    });
    const [splitSearchResults, setSplitSearchResults] = useState<ProductListItem[]>([]);
    const [isSplitSearching, setIsSplitSearching] = useState(false);
    const splitSearchTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
    const { queryProducts } = useProductQuery();

    // Split view search settings state
    const [showSplitSearchSettings, setShowSplitSearchSettings] = useState(false);
    const splitSearchSettingsRef = useRef<HTMLButtonElement>(null);
    const [splitSearchOptions, setSplitSearchOptions] = useState({
        searchProductCategoryNames: true,
        searchTradeNames: true,
        searchSynonyms: true,
        searchChartererNames: true,
        searchCasNumber: true,
    });
    const [splitFilterCriteria, setSplitFilterCriteria] = useState({
        marpolRegulationIsAnnexI: false,
        marpolRegulationIsAnnexIIChapter17: false,
        marpolRegulationIsAnnexIIChapter18: false,
        marpolRegulationIsGas: false,
    });

    const toggleSplitSearchSettings = () => setShowSplitSearchSettings((prev) => !prev);

    const toggleAllSplitSearchOptions = () => {
        const allSelected = Object.values(splitSearchOptions).every((v) => v);
        setSplitSearchOptions({
            searchProductCategoryNames: !allSelected,
            searchTradeNames: !allSelected,
            searchSynonyms: !allSelected,
            searchChartererNames: !allSelected,
            searchCasNumber: !allSelected,
        });
    };

    const toggleAllSplitFilterCriteria = () => {
        const allSelected = Object.values(splitFilterCriteria).every((v) => v);
        setSplitFilterCriteria({
            marpolRegulationIsAnnexI: !allSelected,
            marpolRegulationIsAnnexIIChapter17: !allSelected,
            marpolRegulationIsAnnexIIChapter18: !allSelected,
            marpolRegulationIsGas: !allSelected,
        });
    };

    const handleSplitSearchOptionChange = (option: keyof typeof splitSearchOptions) => {
        setSplitSearchOptions((prev) => ({ ...prev, [option]: !prev[option] }));
    };

    const handleSplitFilterCriteriaChange = (criteria: keyof typeof splitFilterCriteria) => {
        setSplitFilterCriteria((prev) => ({ ...prev, [criteria]: !prev[criteria] }));
    };

    // Initialize productId from URL on mount
    useEffect(() => {
        const initialProductId = getQueryParamValueFromUrl(PRODUCT_PARAMS.PRODUCT_ID);
        if (initialProductId) {
            setProductId(initialProductId);
        }
    }, [getQueryParamValueFromUrl]);

    // Handle newCategory=true query param (navigated from Products list)
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        if (params.get('newCategory') !== 'true' || !canEdit) return;

        // Enable edit mode and set up blank product creation
        setEditMode(true);

        const newCategoryChanges: Partial<ProductDetail> = {
            id: 0,
            tradeName: '',
            charterer: { id: 0, name: '', code: '', externalId: 0, lastUpdatedUtc: new Date().toISOString() },
            externalIds: [],
            isActive: false,
            isApproved: false,
            synonyms: [],
            procedures: [],
            comments: [],
            coatingCompatibilities: [],
            stowageRules: [],
            category: { id: 0, name: '', externalIds: [], regulations: { marpolRegulation: 1 } } as any,
        };

        clearPendingChanges();
        setIsCreatingNewProduct(true);
        setIsCreatingNewCategory(true);
        mergePendingChanges(newCategoryChanges);
        setHasUnsavedChanges(true);

        // Clean newCategory from URL to avoid re-triggering
        params.delete('newCategory');
        const newUrl = params.toString() ? `${window.location.pathname}?${params}` : window.location.pathname;
        window.history.replaceState({}, '', newUrl);
    }, [canEdit, setEditMode, clearPendingChanges, mergePendingChanges, setHasUnsavedChanges]);

    // Handle productId changes from URL query parameters
    useEffect(() => {
        if (!queryParamUpdate?.queryParamTrigger) return;

        const productIdTrigger = queryParamUpdate.queryParamTrigger.find((qp) => qp.name === PRODUCT_PARAMS.PRODUCT_ID);
        if (productIdTrigger?.newValue) {
            setProductId(productIdTrigger.newValue);
        } else if (productIdTrigger?.clearParam) {
            setProductId(null);
        }

        // Handle tab parameter changes
        const tabTrigger = queryParamUpdate.queryParamTrigger.find((qp) => qp.name === PRODUCT_PARAMS.TAB);
        if (tabTrigger?.newValue) {
            const validTabs: TabType[] = [
                'all',
                'general',
                'operational',
                'regulations',
                'uscg',
                'coating',
                'safety',
                'cleaning',
                'other-procedures',
                'external',
                // 'orca', // Hidden - not in use
                'changelog',
            ];
            if (validTabs.includes(tabTrigger.newValue as TabType)) {
                setActiveTab(tabTrigger.newValue as TabType);
            }
        } else if (tabTrigger?.clearParam) {
            setActiveTab('all');
        }
    }, [queryParamUpdate]);

    // Update URL when tab changes
    const handleTabChange = (tab: TabType) => {
        setActiveTab(tab);
        // Update URL with new tab, but only if it's not 'all' (default)
        if (tab === 'all') {
            triggerQueryParamsUpdate([{ name: PRODUCT_PARAMS.TAB, clearParam: true }]);
        } else {
            triggerQueryParamsUpdate([{ name: PRODUCT_PARAMS.TAB, newValue: tab }]);
        }
    };

    // Fetch product when productId changes
    useEffect(() => {
        if (productId) {
            getProduct();
        }
    }, [productId]);

    // Update browser tab title when product changes
    useEffect(() => {
        if (product) {
            const productName = product.tradeName || product.category?.name || '';
            document.title = productName ? `CB - ${productName}` : 'CommodityBook';
        } else {
            document.title = 'CommodityBook';
        }

        return () => {
            document.title = 'CommodityBook';
        };
    }, [product]);

    // Split view search: debounce and fetch results
    useEffect(() => {
        if (splitListMode !== 'search') return;

        // Persist the search term to sessionStorage
        sessionStorage.setItem('cbp.lastSearch', splitSearchTerm);

        if (!splitSearchTerm.trim()) {
            setSplitSearchResults([]);
            setIsSplitSearching(false);
            return;
        }

        if (splitSearchTimeoutRef.current) {
            clearTimeout(splitSearchTimeoutRef.current);
        }

        setIsSplitSearching(true);
        splitSearchTimeoutRef.current = setTimeout(async () => {
            try {
                // Build MARPOL regulation codes from filter criteria
                const marpolRegulationCodes: number[] = [];
                if (splitFilterCriteria.marpolRegulationIsAnnexI) marpolRegulationCodes.push(1);
                if (splitFilterCriteria.marpolRegulationIsAnnexIIChapter17) marpolRegulationCodes.push(2);
                if (splitFilterCriteria.marpolRegulationIsAnnexIIChapter18) marpolRegulationCodes.push(3);
                if (splitFilterCriteria.marpolRegulationIsGas) marpolRegulationCodes.push(4);

                const result = await queryProducts({
                    search: splitSearchTerm.trim(),
                    pageSize: 5000,
                    // Search scope flags — only send false values (true is the default)
                    searchTradeNames: splitSearchOptions.searchTradeNames ? undefined : false,
                    searchCategoryNames: splitSearchOptions.searchProductCategoryNames ? undefined : false,
                    searchSynonyms: splitSearchOptions.searchSynonyms ? undefined : false,
                    searchCasNumber: splitSearchOptions.searchCasNumber ? undefined : false,
                    // MARPOL regulation filter
                    marpolRegulationCodes: marpolRegulationCodes.length > 0 ? marpolRegulationCodes : undefined,
                });

                let items = result.items;

                // Client-side charterer name search (OR semantics with text search)
                if (splitSearchOptions.searchChartererNames && splitSearchTerm.trim() && charterers.length > 0) {
                    const term = splitSearchTerm.trim().toLowerCase();
                    const matchingChartererIds = charterers
                        .filter((c) => c.name.toLowerCase().includes(term))
                        .map((c) => c.id);

                    if (matchingChartererIds.length > 0) {
                        const chartererResult = await queryProducts({
                            chartererIds: matchingChartererIds,
                            pageSize: 5000,
                            marpolRegulationCodes: marpolRegulationCodes.length > 0 ? marpolRegulationCodes : undefined,
                        });
                        const existingIds = new Set(items.map((i) => i.id));
                        const additional = chartererResult.items.filter((i) => !existingIds.has(i.id));
                        items = [...items, ...additional];
                    }
                }

                // Enrich with charterer names from cached list
                items = enrichWithChartererNames(items, charterers);

                // Apply client-side relevance sorting (consistent with products page)
                // Apply writer-role filter then relevance sorting
                let filtered = items;
                if (!hasWriterRole) {
                    filtered = filtered.filter((p) => !!p.tradeName);
                }
                const sorted = sortByRelevance(filtered, splitSearchTerm.trim());
                setSplitSearchResults(sorted);
            } catch (err) {
                console.error('[ProductPage] Split view search failed:', err);
                setSplitSearchResults([]);
            } finally {
                setIsSplitSearching(false);
            }
        }, 300);

        return () => {
            if (splitSearchTimeoutRef.current) {
                clearTimeout(splitSearchTimeoutRef.current);
            }
        };
    }, [splitSearchTerm, splitListMode, queryProducts, splitSearchOptions, splitFilterCriteria, charterers, hasWriterRole]);

    const handleBackClick = () => {
        navigate('/products');
    };

    // Find the category (default) product from related products
    // Must be before early returns to maintain hooks order
    const categoryProduct = useMemo(() => {
        return relatedProducts.find(
            (rp: any) => (!rp.tradeName || rp.tradeName.trim() === '') && !rp.chartererId && (!rp.chartererName || rp.chartererName.trim() === '')
        );
    }, [relatedProducts]);

    // Fetch the category product's Contains value for trade-name products.
    // This fetches the full category product detail so we have its Contains
    // even before the QueryProductMetaData backend is redeployed to return it.
    useEffect(() => {
        if (!categoryProduct?.id || !appSettings?.api) return;
        // If the category product IS the current product, just use its data directly
        if (product && categoryProduct.id === product.id) {
            setCategoryProductContains(product.regulations?.tripartiteAgreement?.contains ?? null);
            return;
        }
        // If QueryProductMetaData already returns contains (backend deployed), use that
        if (categoryProduct.contains) {
            setCategoryProductContains(categoryProduct.contains);
            return;
        }
        // Otherwise fetch the category product's full detail
        let cancelled = false;
        (async () => {
            try {
                const url = buildApiUrl(appSettings.api, `/GetProduct?id=${categoryProduct.id}`);
                const response = await axios.get(url);
                if (!cancelled) {
                    const contains = response.data?.regulations?.tripartiteAgreement?.contains ?? null;
                    setCategoryProductContains(contains);
                }
            } catch (err) {
                console.warn('[ProductPage] Failed to fetch category product contains:', err);
            }
        })();
        return () => { cancelled = true; };
    }, [categoryProduct?.id, categoryProduct?.contains, product, appSettings?.api]);

    // Resolved Contains: use category product's Contains for all products in the category
    const resolvedContains = categoryProductContains ?? product?.regulations?.tripartiteAgreement?.contains ?? null;

    // Track product visit in history (after resolvedContains is available)
    useEffect(() => {
        if (product && product.id) {
            const productMetadata: ProductListItem = {
                id: product.id,
                tradeName: product.tradeName,
                categoryName: product.category?.name || '',
                chartererId: product.charterer?.id,
                chartererName: product.charterer?.id
                    ? charterers.find((c) => c.id === product.charterer?.id)?.name || ''
                    : '',
                casNumber: product.category?.externalIds?.find((e: any) => e.source === 'CAS')?.value || '',
                marpolRegulation: String(product.category?.regulations?.marpolRegulation ?? ''),
                contains: resolvedContains || undefined,
                isActive: product.isActive,
            };
            addToProductHistory(productMetadata);
        }
    }, [product, resolvedContains, charterers]);

    const handleCategoryClick = useCallback(() => {
        if (categoryProduct) {
            triggerQueryParamsUpdate([{ name: PRODUCT_PARAMS.PRODUCT_ID, newValue: String(categoryProduct.id) }]);
        }
    }, [categoryProduct, triggerQueryParamsUpdate]);

    const handleSave = useCallback(async () => {
        // Use blankProduct as fallback when creating a new category (product from API is null)
        const saveProduct = product || (isCreatingNewProduct ? blankProduct : null);
        console.log('[handleSave] START — product:', !!product, 'isCreatingNewProduct:', isCreatingNewProduct, 'isCreatingNewCategory:', isCreatingNewCategory, 'saveProduct:', !!saveProduct);
        if (!saveProduct) {
            console.warn('[handleSave] No product available — aborting save.');
            return;
        }

        // Get all pending changes from context (includes changes from all sections)
        const pendingChanges = getPendingChanges();
        console.log('[handleSave] pendingChanges keys:', Object.keys(pendingChanges));
        console.log('[handleSave] pendingChanges.category:', pendingChanges.category);

        // Charterer validation: non-category products must have a charterer assigned
        // Skip charterer validation when creating a new product category (no trade name needed)
        if (!isCreatingNewCategory && (isCreatingNewProduct || (!saveProduct.tradeName && pendingChanges.tradeName))) {
            const charterer = pendingChanges.charterer;
            if (!charterer || !charterer.id || charterer.id <= 0) {
                alert('A Charterer must be assigned.');
                return;
            }
        }

        setIsSaving(true);
        try {

            // When creating a new product (New/Copy), override the product id to 0
            // so that buildMinimalUpdatePayload produces Id: 0 in the API payload,
            // which tells the UpsertProduct backend to INSERT rather than UPDATE.
            const baseProduct = isCreatingNewProduct
                ? { ...saveProduct, id: 0, isActive: false, isApproved: false }
                : saveProduct;

            // Build payload from pending changes
            // Convert synonyms from Synonym[] to string[] if needed
            const synonymsStrings = pendingChanges.synonyms?.map((syn: any) => (typeof syn === 'string' ? syn : syn.synonymName || syn));

            console.log('[handleSave] pendingChanges.materialSafetyDataSheets:', pendingChanges.materialSafetyDataSheets);
            console.log('[handleSave] pendingChanges.productDocuments:', pendingChanges.productDocuments);

            const payload = buildMinimalUpdatePayload(baseProduct, {
                tradeName: pendingChanges.tradeName,
                synonyms: synonymsStrings,
                casNumber: pendingChanges.externalIds?.find((id: any) => id.source === 'CAS')?.value,
                description: pendingChanges.comments?.find((c: any) => c.category === 4)?.text,
                warning: pendingChanges.comments?.find((c: any) => c.category === 5)?.text,
                charterer: pendingChanges.charterer,
                categoryName: pendingChanges.category?.name,
                // Include all non-description/warning comments (e.g., coating comments)
                additionalComments: pendingChanges.comments
                    ?.filter((c: any) => c.category !== 4 && c.category !== 5)
                    .map((c: any) => ({
                        id: c.id,
                        text: c.text,
                        category: c.category,
                        visibility: c.visibility,
                        lastUpdatedUtc: c.lastUpdatedUtc,
                    })),
                coatingCompatibilities: pendingChanges.coatingCompatibilities,
                procedures: pendingChanges.procedures,
                externalIds: pendingChanges.externalIds,
                stowageRules: pendingChanges.stowageRules,
                temperatureRequirements: pendingChanges.temperatureRequirements,
                regulations: pendingChanges.regulations,
                category: pendingChanges.category,
                physicalData: pendingChanges.physicalData,
                gasPhysicalData: pendingChanges.gasPhysicalData,
                isActive: pendingChanges.isActive,
                materialSafetyDataSheets: pendingChanges.materialSafetyDataSheets,
                productDocuments: pendingChanges.productDocuments,
            });

            console.log('[handleSave] Built payload — Id:', payload.Id, 'CategoryId:', payload.CategoryId, 'Category:', payload.Category);
            console.log('[handleSave] Final payload MaterialSafetyDataSheets:', payload.MaterialSafetyDataSheets);
            console.log('[handleSave] Final payload ProductDocuments:', payload.ProductDocuments);

            // Pre-save validation: check for regulation-based validation before sending
            const preValidation = validateProductForUpsert(payload);
            console.log('[handleSave] Validation result — isValid:', preValidation.isValid, 'errors:', preValidation.errors, 'warnings:', preValidation.warnings);
            if (!preValidation.isValid) {
                alert(`Cannot save:\n${preValidation.errors.join('\n')}`);
                setIsSaving(false);
                return;
            }
            if (preValidation.warnings.length > 0) {
                const proceed = confirm(`Warnings:\n${preValidation.warnings.join('\n')}\n\nDo you want to proceed?`);
                if (!proceed) {
                    setIsSaving(false);
                    return;
                }
            }

            console.log('[handleSave] Sending payload to API...');

            const response = await updateProduct(payload, /* skipValidation */ true);
            if (response.success) {
                clearPendingChanges();
                const wasCreatingNewProduct = isCreatingNewProduct;
                setIsCreatingNewProduct(false);
                setIsCreatingNewCategory(false);

                // If we just created a new product, navigate to it using the ID from the response
                // API returns camelCase properties (via CamelCasePropertyNamesContractResolver)
                const newProductId = response.data?.id ?? response.data?.Id;
                if (wasCreatingNewProduct && newProductId) {
                    triggerQueryParamsUpdate([{ name: PRODUCT_PARAMS.PRODUCT_ID, newValue: String(newProductId) }]);
                } else {
                    // Refresh the existing product data from the server
                    getProduct();
                }
            } else {
                const errorMsg = response.errors?.join(', ') || 'Unknown error';
                alert(`Failed to ${isCreatingNewProduct ? 'create' : 'update'} product: ${errorMsg}`);
            }
        } catch (err) {
            console.error('Save failed:', err);
            alert(`Failed to ${isCreatingNewProduct ? 'create' : 'save'} product`);
        } finally {
            setIsSaving(false);
        }
    }, [product, blankProduct, isCreatingNewProduct, isCreatingNewCategory, getPendingChanges, updateProduct, clearPendingChanges, getProduct, triggerQueryParamsUpdate]);

    const handleCancel = useCallback(() => {
        clearPendingChanges();
        setIsCreatingNewProduct(false);
        setIsCreatingNewCategory(false);
    }, [clearPendingChanges]);

    const handleNewChartererProduct = useCallback(() => {
        if (!product || !canEdit || !isInEditMode) return;

        // Check if there are user-driven unsaved changes (not auto-populated data like coating auto-merge)
        if (hasUnsavedChanges) {
            alert('Please save or discard your changes before creating a new product.');
            return;
        }

        // Confirm the operation
        if (
            !confirm(
                'This will copy all details from this product category into a new charterer-specific product (including External IDs). You will need to fill in the Trade Name and select a Charterer. The product will be marked as inactive. Continue?'
            )
        ) {
            return;
        }

        // Create changes object that represents the new product (includes ExternalIds)
        // id: 0 signals to the backend UpsertProduct API that this is a CREATE, not an UPDATE
        const copiedChanges: Partial<ProductDetail> = {
            id: 0, // New product — backend creates a new record when Id is 0
            tradeName: '',
            charterer: { id: 0, name: '', code: '', externalId: 0, lastUpdatedUtc: new Date().toISOString() },
            externalIds: product.externalIds, // Include external IDs for "new" operation
            isActive: false, // New products start as inactive
            isApproved: false, // New products start as not approved

            synonyms: product.synonyms,
            procedures: product.procedures,
            comments: product.comments,
            coatingCompatibilities: product.coatingCompatibilities,
            stowageRules: product.stowageRules,
            temperatureRequirements: product.temperatureRequirements,
            regulations: product.regulations,
            category: product.category,
            physicalData: product.physicalData,
            gasPhysicalData: product.gasPhysicalData,
            draegerTubes: product.draegerTubes,
            mainHazards: product.mainHazards,
            isNonCargo: product.isNonCargo,
        };

        clearPendingChanges();
        setIsCreatingNewProduct(true);
        setIsCreatingNewCategory(false);
        mergePendingChanges(copiedChanges);
        setHasUnsavedChanges(true);

        window.scrollTo({ top: 0, behavior: 'smooth' });

        alert(
            'New charterer-specific product prepared. The form now contains all details from the product category (including External IDs). Please fill in the Trade Name and assign a Charterer, then click Save to create the new product.'
        );
    }, [product, canEdit, isInEditMode, hasUnsavedChanges, clearPendingChanges, mergePendingChanges, setHasUnsavedChanges]);

    const handleNewProductCategory = useCallback(() => {
        if (!canEdit || !isInEditMode) return;

        // Check if there are user-driven unsaved changes (not auto-populated data like coating auto-merge)
        if (hasUnsavedChanges) {
            alert('Please save or discard your changes before creating a new product category.');
            return;
        }

        if (!confirm('This will create a new blank product category. Continue?')) {
            return;
        }

        // Create a blank product category with id: 0 to signal creation
        const newCategoryChanges: Partial<ProductDetail> = {
            id: 0,
            tradeName: '',
            charterer: { id: 0, name: '', code: '', externalId: 0, lastUpdatedUtc: new Date().toISOString() },
            externalIds: [],
            isActive: false,
            isApproved: false,
            synonyms: [],
            procedures: [],
            comments: [],
            coatingCompatibilities: [],
            stowageRules: [],
            category: { id: 0, name: '', externalIds: [], regulations: { marpolRegulation: 1 } } as any,
        };

        clearPendingChanges();
        setIsCreatingNewProduct(true);
        setIsCreatingNewCategory(true);
        mergePendingChanges(newCategoryChanges);
        setHasUnsavedChanges(true);

        window.scrollTo({ top: 0, behavior: 'smooth' });

        alert(
            'New product category prepared. Fill in the category name and other details, then click Save to create the new product category.'
        );
    }, [canEdit, isInEditMode, hasUnsavedChanges, clearPendingChanges, mergePendingChanges, setHasUnsavedChanges]);

    const handleCopyProduct = useCallback(() => {
        if (!product || !canEdit || !isInEditMode) return;

        // Create changes object that represents the copied product
        // id: 0 signals to the backend UpsertProduct API that this is a CREATE, not an UPDATE
        const copiedChanges: Partial<ProductDetail> = {
            id: 0, // New product — backend creates a new record when Id is 0
            tradeName: '',
            charterer: { id: 0, name: '', code: '', externalId: 0, lastUpdatedUtc: new Date().toISOString() },
            externalIds: [], // Clear external IDs as per requirement
            isActive: false, // New products start as inactive
            isApproved: false, // New products start as not approved

            // Copy all existing data
            synonyms: product.synonyms,
            procedures: product.procedures,
            comments: product.comments,
            coatingCompatibilities: product.coatingCompatibilities,
            stowageRules: product.stowageRules,
            temperatureRequirements: product.temperatureRequirements,
            regulations: product.regulations,
            category: product.category,
            physicalData: product.physicalData,
            gasPhysicalData: product.gasPhysicalData,
            draegerTubes: product.draegerTubes,
            mainHazards: product.mainHazards,
            isNonCargo: product.isNonCargo,
        };

        // Clear existing pending changes and set the copied data
        clearPendingChanges();
        setIsCreatingNewProduct(true);
        setIsCreatingNewCategory(false);
        // Use mergePendingChanges to apply all copied data at once
        mergePendingChanges(copiedChanges);

        // Mark form as having changes so save button is enabled
        setHasUnsavedChanges(true);

        // Scroll to top to show the form
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, [product, canEdit, isInEditMode, clearPendingChanges, mergePendingChanges, setHasUnsavedChanges]);

    // Effective product: use the loaded product, or the blank product when creating a new category
    const effectiveProduct = product || (isCreatingNewProduct ? blankProduct : null);

    // Format category name by replacing "trade name ...." and "contains ...." placeholders with actual values
    const formattedCategoryName = effectiveProduct ? formatCategoryName(
        effectiveProduct.category?.name,
        effectiveProduct.tradeName,
        resolvedContains
    ) : '';

    // In split view, skip full-page loading/error states so the panel stays visible.
    // Instead, a loading spinner is shown inline in the content area.
    if (!isSplitView || !effectiveProduct) {
        if (isLoading) {
            return (
                <ProductPageShell>
                    <LoadingContainer>
                        <LoadingSpinner />
                        <p>Loading product details...</p>
                    </LoadingContainer>
                </ProductPageShell>
            );
        }

        if (error) {
            return (
                <ProductPageShell>
                    <ErrorContainer>
                        <h2>Error Loading Product</h2>
                        <p>{error}</p>
                        <ErrorActions>
                            <RetryButton onClick={() => getProduct()}>Retry</RetryButton>
                            <BackButton onClick={handleBackClick}>
                                <FontAwesomeIcon icon={faChevronLeft} /> Back to Products
                            </BackButton>
                        </ErrorActions>
                    </ErrorContainer>
                </ProductPageShell>
            );
        }

        if (!effectiveProduct) {
            return (
                <ProductPageShell>
                    <NotFoundContainer>
                        <h2>Product Not Found</h2>
                        <p>The requested product could not be found.</p>
                        <BackButton onClick={handleBackClick}>
                            <FontAwesomeIcon icon={faChevronLeft} /> Back to Products
                        </BackButton>
                    </NotFoundContainer>
                </ProductPageShell>
            );
        }
    }

    const renderTemperatureRange = (range: any) => {
        if (!range) return 'N/A';
        const min = range.minValue?.value;
        const max = range.maxValue?.value;
        if (min && max) return `${min}°C - ${max}°C`;
        if (min) return `${min}°C+`;
        if (max) return `≤${max}°C`;
        return 'N/A';
    };

    // Check if this is a trade name product (not a category product)
    const isProductCategory = effectiveProduct.id === effectiveProduct.categoryId || !effectiveProduct.tradeName;

    // Non-writer users cannot view product categories (default products)
    if (!hasWriterRole && isProductCategory) {
        return (
            <ProductPageShell>
                <NotFoundContainer>
                    <h2>Access Restricted</h2>
                    <p>You do not have permission to view product categories.</p>
                    <BackButton onClick={handleBackClick}>
                        <FontAwesomeIcon icon={faChevronLeft} /> Back to Products
                    </BackButton>
                </NotFoundContainer>
            </ProductPageShell>
        );
    }

    const showCopyButton = !isProductCategory; // Only show for trade name products
    const showNewButton = isProductCategory && !!effectiveProduct.categoryId; // Only show for category products

    const showCategoryButton = hasWriterRole && !isProductCategory && !!categoryProduct;

    return (
        <ProductPageShell>
            {isSplitView ? (
                <SplitViewContainer>
                    <SplitViewProductListPanel>
                        <SplitViewTabBar>
                            <SplitViewTab
                                $active={splitListMode === 'history'}
                                onClick={() => setSplitListMode('history')}
                                title="Previously visited products"
                            >
                                <FontAwesomeIcon icon={faHistory} /> Last visited
                            </SplitViewTab>
                            <SplitViewTab
                                $active={splitListMode === 'search'}
                                onClick={() => setSplitListMode('search')}
                                title="Search all products"
                            >
                                <FontAwesomeIcon icon={faSearch} /> Search
                            </SplitViewTab>
                            <SplitViewTabBarRight>
                                <SplitViewFilterButton
                                    $active={showSplitSearchSettings}
                                    onClick={toggleSplitSearchSettings}
                                    title="Filter options"
                                    ref={splitSearchSettingsRef}
                                >
                                    <FontAwesomeIcon icon={faFilter} />
                                </SplitViewFilterButton>
                                <SplitViewCloseButton
                                    onClick={() => setIsSplitView(false)}
                                    title="Close list panel"
                                >
                                    <FontAwesomeIcon icon={faTimes} />
                                </SplitViewCloseButton>
                            </SplitViewTabBarRight>
                        </SplitViewTabBar>
                        {showSplitSearchSettings && (
                            <SplitViewFilterPanel>
                                <SplitViewFilterSection>
                                    <SplitViewFilterSectionTitle>Search In:</SplitViewFilterSectionTitle>
                                    <SplitViewFilterCheckboxGroup>
                                        <label>
                                            <input
                                                type="checkbox"
                                                checked={Object.values(splitSearchOptions).every((v) => v)}
                                                onChange={toggleAllSplitSearchOptions}
                                            />
                                            (Select All)
                                        </label>
                                        <label>
                                            <input
                                                type="checkbox"
                                                checked={splitSearchOptions.searchProductCategoryNames}
                                                onChange={() => handleSplitSearchOptionChange('searchProductCategoryNames')}
                                            />
                                            Product
                                        </label>
                                        <label>
                                            <input
                                                type="checkbox"
                                                checked={splitSearchOptions.searchTradeNames}
                                                onChange={() => handleSplitSearchOptionChange('searchTradeNames')}
                                            />
                                            Trade Name
                                        </label>
                                        <label>
                                            <input
                                                type="checkbox"
                                                checked={splitSearchOptions.searchSynonyms}
                                                onChange={() => handleSplitSearchOptionChange('searchSynonyms')}
                                            />
                                            Synonyms
                                        </label>
                                        <label>
                                            <input
                                                type="checkbox"
                                                checked={splitSearchOptions.searchChartererNames}
                                                onChange={() => handleSplitSearchOptionChange('searchChartererNames')}
                                            />
                                            Charterer
                                        </label>
                                        <label>
                                            <input
                                                type="checkbox"
                                                checked={splitSearchOptions.searchCasNumber}
                                                onChange={() => handleSplitSearchOptionChange('searchCasNumber')}
                                            />
                                            CAS number
                                        </label>
                                    </SplitViewFilterCheckboxGroup>
                                </SplitViewFilterSection>
                                <SplitViewFilterSection>
                                    <SplitViewFilterSectionTitle>Regulation:</SplitViewFilterSectionTitle>
                                    <SplitViewFilterCheckboxGroup>
                                        <label>
                                            <input
                                                type="checkbox"
                                                checked={Object.values(splitFilterCriteria).every((v) => v)}
                                                onChange={toggleAllSplitFilterCriteria}
                                            />
                                            (Select All)
                                        </label>
                                        <label>
                                            <input
                                                type="checkbox"
                                                checked={splitFilterCriteria.marpolRegulationIsAnnexI}
                                                onChange={() => handleSplitFilterCriteriaChange('marpolRegulationIsAnnexI')}
                                            />
                                            Annex I
                                        </label>
                                        <label>
                                            <input
                                                type="checkbox"
                                                checked={splitFilterCriteria.marpolRegulationIsAnnexIIChapter17}
                                                onChange={() => handleSplitFilterCriteriaChange('marpolRegulationIsAnnexIIChapter17')}
                                            />
                                            Annex II Chapter 17
                                        </label>
                                        <label>
                                            <input
                                                type="checkbox"
                                                checked={splitFilterCriteria.marpolRegulationIsAnnexIIChapter18}
                                                onChange={() => handleSplitFilterCriteriaChange('marpolRegulationIsAnnexIIChapter18')}
                                            />
                                            Annex II Chapter 18
                                        </label>
                                        <label>
                                            <input
                                                type="checkbox"
                                                checked={splitFilterCriteria.marpolRegulationIsGas}
                                                onChange={() => handleSplitFilterCriteriaChange('marpolRegulationIsGas')}
                                            />
                                            Gas
                                        </label>
                                    </SplitViewFilterCheckboxGroup>
                                </SplitViewFilterSection>
                            </SplitViewFilterPanel>
                        )}

                        {splitListMode === 'search' && (
                            <SplitViewSearchContainer>
                                <SplitViewSearchInput
                                    type="text"
                                    placeholder="Search products..."
                                    value={splitSearchTerm}
                                    onChange={(e) => setSplitSearchTerm(e.target.value)}
                                    autoFocus
                                />
                            </SplitViewSearchContainer>
                        )}

                        {splitListMode === 'search' && (
                            <ProductCardList
                                items={splitSearchTerm.trim() ? splitSearchResults : []}
                                activeProductId={effectiveProduct.id}
                                onProductClick={(p) => triggerQueryParamsUpdate([{ name: PRODUCT_PARAMS.PRODUCT_ID, newValue: String(p.id) }])}
                                isLoading={isSplitSearching}
                                loadingMessage="Searching..."
                                emptyMessage={splitSearchTerm.trim() ? 'No products found.' : 'Type to search products...'}
                                searchTerm={splitSearchTerm}
                            />
                        )}

                        {splitListMode === 'history' && (
                            <ProductCardList
                                items={hasWriterRole ? getProductHistory() : getProductHistory().filter((p) => !!p.tradeName)}
                                activeProductId={effectiveProduct.id}
                                onProductClick={(p) => triggerQueryParamsUpdate([{ name: PRODUCT_PARAMS.PRODUCT_ID, newValue: String(p.id) }])}
                                emptyMessage="No recently visited products."
                            />
                        )}
                    </SplitViewProductListPanel>
                    <SplitViewMainContent>
                        <ProductHeader
                            title={isCreatingNewProduct ? (isCreatingNewCategory ? 'New Product Category' : 'New Charterer Specific Product') : (effectiveProduct.tradeName || formattedCategoryName || 'Unknown Product')}
                            subtitle={isCreatingNewProduct ? undefined : (effectiveProduct.category && !effectiveProduct.tradeName ? <DefaultProductLabel> [default product]</DefaultProductLabel> : undefined)}
                            isActive={effectiveProduct.isActive}
                            product={effectiveProduct}
                            onBackClick={handleBackClick}
                            backButtonTitle="Back to Products"
                            showPrintButton={true}
                            showSplitViewButton={false}
                            hideBackButton={false}
                            isSplitView={isSplitView}
                            onSplitViewToggle={() => setIsSplitView((prev) => !prev)}
                            showCategoryButton={showCategoryButton}
                            categoryProductName={formatCategoryName(categoryProduct?.categoryName, effectiveProduct.tradeName, resolvedContains) || formattedCategoryName}
                            onCategoryClick={handleCategoryClick}
                            showStatusBadge={true}
                            showEditButtons={true}
                            showCopyButton={showCopyButton}
                            showNewButton={showNewButton}
                            onSave={handleSave}
                            onCancel={handleCancel}
                            onCopy={handleCopyProduct}
                            onNewProduct={handleNewChartererProduct}
                            isSaving={isSaving}
                            hasUnsavedChanges={hasUnsavedChanges}
                        />
                        {isLoading ? (
                            <SplitViewContentLoading>
                                <LoadingSpinner />
                                <p>Loading product...</p>
                            </SplitViewContentLoading>
                        ) : (
                        <ProductContent>
                            <TabMenu activeTab={activeTab} onTabChange={handleTabChange} hasWriterRole={hasWriterRole} />
                            <ProductLayout style={activeTab === 'changelog' ? { gridTemplateColumns: '1fr' } : undefined}>
                                {activeTab !== 'changelog' && (
                                    <Sidebar>
                                        <PhysicalPropertiesSection product={effectiveProduct} />
                                        <RulesRegulationsSection product={effectiveProduct} />
                                    </Sidebar>
                                )}
                                <MainContent>
                                    <ProductSections>
                                        <div style={{ display: (activeTab === 'all' || activeTab === 'general') ? undefined : 'none' }}>
                                            <GeneralInformationSection product={effectiveProduct} relatedProducts={relatedProducts} onProductUpdated={getProduct} isCreatingNewProduct={isCreatingNewProduct} isCreatingNewCategory={isCreatingNewCategory} resolvedContains={resolvedContains} />
                                        </div>
                                        <div style={{ display: (activeTab === 'all' || activeTab === 'operational') ? undefined : 'none' }}>
                                            <OperationalHeatingSection product={effectiveProduct} renderTemperatureRange={renderTemperatureRange} />
                                        </div>
                                        <div style={{ display: (activeTab === 'all' || activeTab === 'regulations') ? undefined : 'none' }}>
                                            <RegulationsSection product={effectiveProduct} />
                                        </div>
                                        <div style={{ display: (activeTab === 'all' || activeTab === 'uscg') ? undefined : 'none' }}>
                                            <USCGCompatibilitySection product={effectiveProduct} />
                                        </div>
                                        <div style={{ display: (activeTab === 'all' || activeTab === 'safety') ? undefined : 'none' }}>
                                            <SafetyInformationSection product={effectiveProduct} isProductCategory={!effectiveProduct.tradeName} />
                                        </div>
                                        <div className="print-break-before" style={{ display: (activeTab === 'all' || activeTab === 'coating') ? undefined : 'none' }}>
                                            <CoatingCompatibilitiesSection
                                                product={effectiveProduct}
                                                onCategoryClick={showCategoryButton ? handleCategoryClick : undefined}
                                                categoryProductName={formatCategoryName(categoryProduct?.categoryName, effectiveProduct.tradeName, resolvedContains) || formattedCategoryName}
                                                categoryProductId={categoryProduct?.id}
                                            />
                                        </div>
                                        <div className="print-break-before" style={{ display: (activeTab === 'all' || activeTab === 'cleaning') ? undefined : 'none' }}>
                                            <ProceduresSection product={effectiveProduct} />
                                        </div>
                                        <div style={{ display: (activeTab === 'all' || activeTab === 'other-procedures') ? undefined : 'none' }}>
                                            <OtherProceduresSection product={effectiveProduct} />
                                        </div>
                                        <div style={{ display: activeTab === 'all' ? undefined : 'none' }}>
                                            <CommentsSection comments={effectiveProduct.comments} />
                                        </div>
                                        <div style={{ display: (activeTab === 'all' || activeTab === 'safety') ? undefined : 'none' }}>
                                            <MaterialSafetyDataSheetsSection msds={effectiveProduct.materialSafetyDataSheets} />
                                        </div>
                                        <div style={{ display: (activeTab === 'all' || activeTab === 'external') ? undefined : 'none' }}>
                                            <ExternalIdsSection product={effectiveProduct} />
                                        </div>
                                        {/* ORCA section hidden - not in use
                                        <div style={{ display: (activeTab === 'all' || activeTab === 'orca') ? undefined : 'none' }}>
                                            <ORCASection product={effectiveProduct} />
                                        </div>
                                        */}
                                        <div style={{ display: activeTab === 'physical-properties' ? undefined : 'none' }}>
                                            <PhysicalPropertiesSection product={effectiveProduct} />
                                        </div>
                                        <div style={{ display: activeTab === 'rules-regulations' ? undefined : 'none' }}>
                                            <RulesRegulationsSection product={effectiveProduct} />
                                        </div>
                                        {hasWriterRole && (
                                            <div style={{ display: activeTab === 'changelog' ? undefined : 'none' }}>
                                                <ProductChangeLogSection productDisplayName={effectiveProduct.tradeName || formattedCategoryName || ''} />
                                            </div>
                                        )}
                                    </ProductSections>
                                </MainContent>
                            </ProductLayout>
                        </ProductContent>
                        )}
                    </SplitViewMainContent>
                </SplitViewContainer>
            ) : (
            <>
            <ProductHeader
                title={isCreatingNewProduct ? (isCreatingNewCategory ? 'New Product Category' : 'New Charterer Specific Product') : (effectiveProduct.tradeName || formattedCategoryName || 'Unknown Product')}
                subtitle={isCreatingNewProduct ? undefined : (effectiveProduct.category && !effectiveProduct.tradeName ? <DefaultProductLabel> [default product]</DefaultProductLabel> : undefined)}
                isActive={effectiveProduct.isActive}
                product={effectiveProduct}
                onBackClick={handleBackClick}
                backButtonTitle="Back to Products"
                showPrintButton={true}
                showSplitViewButton={true}
                hideBackButton={false}
                isSplitView={isSplitView}
                onSplitViewToggle={() => setIsSplitView((prev) => !prev)}
                showCategoryButton={showCategoryButton}
                categoryProductName={formatCategoryName(categoryProduct?.categoryName, effectiveProduct.tradeName, resolvedContains) || formattedCategoryName}
                onCategoryClick={handleCategoryClick}
                showStatusBadge={true}
                showEditButtons={true}
                showCopyButton={showCopyButton}
                showNewButton={showNewButton}
                onSave={handleSave}
                onCancel={handleCancel}
                onCopy={handleCopyProduct}
                onNewProduct={handleNewChartererProduct}
                isSaving={isSaving}
                hasUnsavedChanges={hasUnsavedChanges}
            />
            <ProductContent>
                <TabMenu activeTab={activeTab} onTabChange={handleTabChange} hasWriterRole={hasWriterRole} />

                <ProductLayout style={activeTab === 'changelog' ? { gridTemplateColumns: '1fr' } : undefined}>
                    {activeTab !== 'changelog' && (
                        <Sidebar>
                            <PhysicalPropertiesSection product={effectiveProduct} />
                            <RulesRegulationsSection product={effectiveProduct} />
                        </Sidebar>
                    )}

                    <MainContent>
                        <ProductSections>
                            <div style={{ display: (activeTab === 'all' || activeTab === 'general') ? undefined : 'none' }}>
                                <GeneralInformationSection product={effectiveProduct} relatedProducts={relatedProducts} onProductUpdated={getProduct} isCreatingNewProduct={isCreatingNewProduct} isCreatingNewCategory={isCreatingNewCategory} resolvedContains={resolvedContains} />
                            </div>
                            <div style={{ display: (activeTab === 'all' || activeTab === 'operational') ? undefined : 'none' }}>
                                <OperationalHeatingSection product={effectiveProduct} renderTemperatureRange={renderTemperatureRange} />
                            </div>
                            <div style={{ display: (activeTab === 'all' || activeTab === 'regulations') ? undefined : 'none' }}>
                                <RegulationsSection product={effectiveProduct} />
                            </div>
                            <div style={{ display: (activeTab === 'all' || activeTab === 'uscg') ? undefined : 'none' }}>
                                <USCGCompatibilitySection product={effectiveProduct} />
                            </div>
                            <div style={{ display: (activeTab === 'all' || activeTab === 'safety') ? undefined : 'none' }}>
                                <SafetyInformationSection product={effectiveProduct} isProductCategory={!effectiveProduct.tradeName} />
                            </div>
                            <div className="print-break-before" style={{ display: (activeTab === 'all' || activeTab === 'coating') ? undefined : 'none' }}>
                                <CoatingCompatibilitiesSection
                                    product={effectiveProduct}
                                    onCategoryClick={showCategoryButton ? handleCategoryClick : undefined}
                                    categoryProductName={formatCategoryName(categoryProduct?.categoryName, effectiveProduct.tradeName, resolvedContains) || formattedCategoryName}
                                    categoryProductId={categoryProduct?.id}
                                />
                            </div>
                            <div className="print-break-before" style={{ display: (activeTab === 'all' || activeTab === 'cleaning') ? undefined : 'none' }}>
                                <ProceduresSection product={effectiveProduct} />
                            </div>
                            <div style={{ display: (activeTab === 'all' || activeTab === 'other-procedures') ? undefined : 'none' }}>
                                <OtherProceduresSection product={effectiveProduct} />
                            </div>
                            <div style={{ display: activeTab === 'all' ? undefined : 'none' }}>
                                <CommentsSection comments={effectiveProduct.comments} />
                            </div>
                            <div style={{ display: (activeTab === 'all' || activeTab === 'safety') ? undefined : 'none' }}>
                                <MaterialSafetyDataSheetsSection msds={effectiveProduct.materialSafetyDataSheets} />
                            </div>
                            <div style={{ display: (activeTab === 'all' || activeTab === 'external') ? undefined : 'none' }}>
                                <ExternalIdsSection product={effectiveProduct} />
                            </div>
                            {/* ORCA section hidden - not in use
                            <div style={{ display: (activeTab === 'all' || activeTab === 'orca') ? undefined : 'none' }}>
                                <ORCASection product={effectiveProduct} />
                            </div>
                            */}
                            <div style={{ display: activeTab === 'physical-properties' ? undefined : 'none' }}>
                                <PhysicalPropertiesSection product={effectiveProduct} />
                            </div>
                            <div style={{ display: activeTab === 'rules-regulations' ? undefined : 'none' }}>
                                <RulesRegulationsSection product={effectiveProduct} />
                            </div>
                            {hasWriterRole && (
                                <div style={{ display: activeTab === 'changelog' ? undefined : 'none' }}>
                                    <ProductChangeLogSection productDisplayName={effectiveProduct.tradeName || formattedCategoryName || ''} />
                                </div>
                            )}
                        </ProductSections>
                    </MainContent>
                </ProductLayout>
            </ProductContent>
            </>
            )}
        </ProductPageShell>
    );
};

// Wrap with ProductEditContextProvider to enable save coordination between sections
const ProductPageWithContext: React.FC = () => (
    <ProductEditContextProvider>
        <ProductPage />
    </ProductEditContextProvider>
);

export default ProductPageWithContext;
